import { AlertTriangle, Sparkles, XCircle } from 'lucide-react';
import usePublicEconomics from '@/hooks/usePublicEconomics';

/**
 * Mandatory explicit core copywriting blocks — the owner's wording, verbatim. Block 1 is the original;
 * blocks 2 and 3 are Ron's relaunch copy (29 Sep 2026). Do NOT alter wording, numbering, or punctuation.
 */
const BLOCK_ONE = `Whether a program uses a 2×2, a 5×5, a multi-level stepping matrix, or a straight line, traditional setups rely on chronological tracking. Positions are locked into a grid based on when they were bought. As the structure grows, progress slows to a crawl, momentum dies, and late joiners get left behind in a stalled queue.`;

const BLOCK_TWO = `1SimpleCycler 3.0 eliminates the single, frozen company bottleneck. Instead of forcing everyone into an endless static line, the engine alternates continuously between two powerful heartbeats: one heartbeat executes a completely Random Draw from the global pool, and the next heartbeat honors classic First-In, First-Out (FIFO). This ensures new entries get instant opportunity while earlier positions are steadily and systematically rewarded. Combined with automated, time-released Sub Packs, fresh positions drip into the system around the clock.`;

/** Block 3 names the live position cost (admin settings), so it is built per render. */
const blockThree = (positionCost) =>
  `Instead of a massive, stalled company line, the backend processes independent 3×1 cycles where each root position needs just 3 micro-fillers (${positionCost} each) to complete and pay out. Every heartbeat alternates between clearing positions randomly and advancing the FIFO queue, keeping the line moving from both directions. Even better, our 1-Year (52-Week) Sub Packs release positions consistently for 365 days straight—meaning the volume of active dripping packs compounds over time and never runs out of fuel.`;

const CopyBlock = ({ index, title, body, icon: Icon, tone, testid }) => {
  return (
    <div className="sc-glass rounded-2xl p-7 md:p-9 relative overflow-hidden" data-testid={testid}>
      <div className="flex items-start gap-5">
        <div
          className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center border"
          style={{
            background: tone === 'danger' ? 'rgba(255, 90, 90, 0.06)' : tone === 'hero' ? 'rgba(255, 182, 18, 0.12)' : 'rgba(255, 182, 18, 0.06)',
            borderColor: tone === 'danger' ? 'rgba(255, 90, 90, 0.32)' : 'rgba(255, 182, 18, 0.32)',
          }}
        >
          <Icon
            className="w-5 h-5"
            style={{
              color: tone === 'danger' ? '#ff8a8a' : tone === 'hero' ? '#FFC20E' : '#FFB612',
            }}
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--sc-text-mute)]">Block {index}</div>
          <h3 className="font-display font-bold text-xl md:text-2xl mt-1 leading-snug">{title}</h3>
        </div>
      </div>
      <div className="sc-divider my-6" />
      <p className="text-[15px] md:text-[16px] leading-[1.75] text-[var(--sc-text-dim)] whitespace-pre-line">{body}</p>
    </div>
  );
};

const CopySection = () => {
  const { positionCost } = usePublicEconomics();
  return (
    <section id="why" className="relative max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-20 pb-12 md:pb-20" data-testid="copy-section">
      <div className="max-w-3xl">
        <div className="sc-pill">
          <span className="sc-dot" />
          Mandatory Brief
        </div>
        <h2 className="font-display mt-5 text-[34px] md:text-[52px] leading-[1.05] font-bold tracking-tight">
          Why 3.0 Changes Everything: <span className="sc-gold-text">Shuttering the Traditional Bottleneck.</span>
        </h2>
        <p className="mt-5 text-[var(--sc-text-dim)] text-[16px] leading-relaxed">Three plainly written blocks. No marketing fluff. Read them in order.</p>
      </div>

      <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <CopyBlock index={1} tone="danger" icon={XCircle} title="The Flaw in Traditional Systems" body={BLOCK_ONE} testid="copy-block-1" />
        <CopyBlock index={2} tone="hero" icon={Sparkles} title="The 3.0 Solution: The Dual-Heartbeat Advantage" body={BLOCK_TWO} testid="copy-block-2" />
        <CopyBlock
          index={3}
          tone="gold"
          icon={AlertTriangle}
          title="How the Alternating Engine & 52-Week Sub Packs Protect You"
          body={blockThree(positionCost)}
          testid="copy-block-3"
        />
      </div>
    </section>
  );
};

export default CopySection;
